import React, { useRef, useEffect } from "react";
import AvatarCircle from "./ui/AvatarCircle";

interface AvatarPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  currentAvatarId?: string;
}

// Avatar manifest — expand as needed
const AVATARS = [
  { id: "avatar_default", label: "Pick Your Avatar" },
  { id: "avatar_capybara_boy", label: "Capybara Boy" },
  { id: "avatar_capybara_girl", label: "Capybara Girl" },
  { id: "avatar_cow", label: "Cow" }, 
  { id: "avatar_pig", label: "Pig" },
  { id: "avatar_yellowchick", label: "Yellow Chick" },
  { id: "avatar_panda", label: "Panda" }, 
  { id: "avatar_panda_girl", label: "Panda Girl" }, 
  { id: "avatar_sloth", label: "Sloth" }, 
  { id: "avatar_sloth_girl", label: "Sloth Girl" }, 
  { id: "avatar_elephant", label: "Elephant" }, 
  { id: "avatar_elephant_girl", label: "Elephant Girl" }, 
  { id: "avatar_tiger", label: "Tiger" }, 
  { id: "avatar_tiger_cub", label: "Tiger Cub" }, 
  { id: "avatar_rabbit", label: "Rabbit" },
  { id: "avatar_bunny", label: "Bunny" },
  { id: "avatar_giraffe", label: "Giraffe" },
  { id: "avatar_monkey", label: "Monkey" }, 
  { id: "avatar_monkey_girl", label: "Monkey Girl" },
  { id: "avatar_dog", label: "Dog" },
  { id: "avatar_poppy", label: "Poppy" },
  { id: "avatar_cat", label: "Cat" },
  { id: "avatar_kitten", label: "Kitten" },
  { id: "avatar_lion", label: "Lion" }, 
  { id: "avatar_lion_cub", label: "Lion Cub" }, 
  { id: "avatar_koala", label: "Koala" }, 
  { id: "avatar_koala_girl", label: "Koala Girl" }, 
  { id: "avatar_penguin", label: "Penguin" }, 
  { id: "avatar_penguin_girl", label: "Penguin Girl" }, 
  { id: "avatar_meerkat", label: "Meerkat" }, 
  { id: "avatar_meerkat_girl", label: "Meerkat Girl" }, 
  { id: "avatar_redpanda", label: "Red Panda" }, 
  { id: "avatar_redpanda_girl", label: "Red Panda Girl" }, 
  { id: "avatar_otter", label: "Otter" }, 
  { id: "avatar_otter_girl", label: "Otter Girl" }, 
  { id: "avatar_polarbear", label: "Polar Bear" }, 
  { id: "avatar_polarbear_girl", label: "Polar Bear Girl" }, 
  { id: "avatar_dolphin", label: "Dolphin" }, 
  { id: "avatar_dolphin_calf", label: "Dolphin Calf" }, 
  { id: "avatar_fox", label: "Fox" },
  { id: "avatar_guinea_pig_baby", label: "Guinea Pig Baby" },
  { id: "avatar_guinea_pig", label: "Guinea Pig" },
];

export default function AvatarPickerModal({
  open,
  onClose,
  currentAvatarId,
  onSelect,
}: AvatarPickerModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement | null>(null);

  // Auto-scroll selected avatar into view when opening
  useEffect(() => {
    if (open && selectedRef.current && containerRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Trigger a gentle highlight animation
      selectedRef.current.classList.add("animate-avatarPulse");

      // Remove the animation class after finishing (clean for next open)
      const timeout = setTimeout(() => {
        selectedRef.current?.classList.remove("animate-avatarPulse");
      }, 600);

      return () => clearTimeout(timeout);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <button
        aria-label="Close avatar picker"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ring-black/5 dark:bg-zinc-900 dark:text-zinc-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="avatar-picker-title"
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 id="avatar-picker-title" className="text-xl font-semibold">
            Choose an Avatar
          </h2>
          <button
            onClick={onClose}
            className="rounded-full px-3 py-1 text-sm text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Scrollable avatar grid */}
        <div
          className="max-h-[360px] overflow-y-auto pr-1"
          ref={containerRef}
        >
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-6 justify-items-center py-4">
            {AVATARS.map((a) => {
              const selected = a.id === currentAvatarId;

              return (
                <button
                  key={a.id}
                  ref={selected ? selectedRef : null}
                  type="button"
                  onClick={() => {
                    onSelect(a.id);
                    onClose();
                  }}
                  className={`flex flex-col items-center p-2 rounded-xl transition 
                    ${
                      selected
                        ? "bg-zinc-100 dark:bg-zinc-800 ring-2 ring-blue-500"
                        : "hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    }
                  `}
                >
                  <AvatarCircle avatarId={a.id} size={64} />
                  <span className="mt-2 text-xs text-zinc-600 dark:text-zinc-300">
                    {a.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-xl border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* ✨ Highlight animation style */}
      <style>
        {`
          @keyframes avatarPulse {
            0% { transform: scale(1); box-shadow: 0 0 0 rgba(59,130,246,0); }
            40% { transform: scale(1.05); box-shadow: 0 0 12px rgba(59,130,246,0.45); }
            100% { transform: scale(1); box-shadow: 0 0 0 rgba(59,130,246,0); }
          }
          .animate-avatarPulse {
            animation: avatarPulse 0.6s ease-out;
          }
        `}
      </style>
    </div>
  );
}
